import React, { useState, useMemo, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, Building2, Filter, ChevronDown, CreditCard, Calendar, Users, RefreshCw } from 'lucide-react';
import DataTable from '../../../../components/common/DataTable';
import DeleteConfirmModal from '../../../../components/common/Modal/DeleteConfirmModal';
import CompanyEditModal from './CompanyEditModal';
import RegistrationSuccessModal from './RegistrationSuccessModal';
import { companyService } from '../../../../services/companyService';
import { getPlanById } from '../../../../config/subscriptionPlans';
import { getCompanyStatusColor, getCompanyStatusLabel } from '../../../../utils/companyUtils';

const CompaniesView = ({ onViewCompany }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedPlan, setSelectedPlan] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [showPlanDropdown, setShowPlanDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [editCompany, setEditCompany] = useState(null);
    const [successCompany, setSuccessCompany] = useState(null);
    const [deleteCompany, setDeleteCompany] = useState(null);

    // We don't auto-load on mount if serverSide=true handles the initial fetch via onParamsChange.
    // However, DataTable might trigger onParamsChange on mount.
    // Let's rely on onParamsChange for all loading.
    const loadCompanies = async (search = searchTerm, page = currentPage, size = pageSize) => {
        try {
            setLoading(true);
            setError(null);
            
            // map search filter 
            const fetchParams = {
                page: page - 1,
                size: size,
                search: search
            };

            if (selectedPlan !== 'All') {
                fetchParams.plan = selectedPlan;
            }

            if (selectedStatus !== 'All') {
                fetchParams.status = selectedStatus;
            }

            const data = await companyService.getCompanies(fetchParams);
            setCompanies(data.content || data || []);
            setTotalElements(data.totalElements || data?.length || 0);
        } catch (err) {
            console.error('Failed to load companies:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load companies';
            setError(errorMessage);
            setCompanies([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    };

    // Refetch when filters or search change
    useEffect(() => {
        const delay = setTimeout(() => {
            loadCompanies();
        }, 300);
        return () => clearTimeout(delay);
    }, [selectedPlan, selectedStatus, searchTerm, currentPage, pageSize]);

    const plans = ['All', 'basic', 'professional', 'enterprise'];
    const statuses = ['All', 'active', 'expired', 'expiring_soon', 'suspended'];

    // Filtering is now handled server-side, so filteredCompanies is just companies
    const filteredCompanies = companies;

    const handleAddCompany = () => {
        setEditCompany({
            id: null,
            name: '',
            tradeLicenseNo: '',
            businessType: 'Wholesale',
            category: '',
            description: '',
            contactPerson: '',
            email: '',
            phone: '',
            whatsapp: '',
            addressLine: '',
            city: 'Dubai',
            country: 'UAE',
            subscriptionPlan: 'basic',
            subscriptionStartDate: new Date().toISOString().split('T')[0],
            subscriptionExpiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            status: 'active'
        });
    };

    const handleEditCompany = (company) => {
        setEditCompany(company);
    };

    const handleViewCompany = (company) => {
        if (onViewCompany) {
            onViewCompany(company);
        }
    };

    const handleDeleteCompany = (company) => {
        setDeleteCompany(company);
    };

    const confirmDelete = async () => {
        try {
            await companyService.deleteCompany(deleteCompany.id);
            await loadCompanies();
            setDeleteCompany(null);
        } catch (error) {
            console.error('Failed to delete company:', error);
            alert('Failed to delete company');
        }
    };

    const handleSaveCompany = async (companyData) => {
        try {
            if (companyData.id) {
                await companyService.updateCompany(companyData.id, companyData);
                await loadCompanies();
                setEditCompany(null);
            } else {
                // Register new company
                const result = await companyService.createCompany(companyData);
                console.log('[CompaniesView] Registration result:', result);

                await loadCompanies();
                setEditCompany(null);

                // Show success modal with credentials
                setSuccessCompany({
                    ...companyData,
                    ...result,
                    credentials: result.credentials
                });
            }
        } catch (error) {
            console.error('Failed to save company:', error);
            alert(error.message || 'Failed to save company. Please try again.');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getDaysUntilExpiry = (expiryDate) => {
        const expiry = new Date(expiryDate);
        const today = new Date();
        const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        return days;
    };

    // Search filter function is ignored in serverSide mode, but we keep it for signature
    const searchFilter = () => true;

    // Table columns configuration
    const columns = [
        {
            header: 'Company',
            field: 'name',
            sortable: true
        },
        {
            header: 'Supplier ID',
            field: 'supplierId',
            sortable: true
        },
        {
            header: 'Email',
            field: 'email',
            sortable: true
        },
        {
            header: 'Plan',
            field: 'subscriptionPlan',
            sortable: true
        },
        {
            header: 'Status',
            field: 'status',
            sortable: true
        },
        {
            header: 'Limits',
            field: null,
            sortable: false
        },
        {
            header: 'Actions',
            field: null,
            sortable: false
        }
    ];

    // Render table row
    const renderRow = (company) => {
        const plan = getPlanById(company.subscriptionPlan);

        return (
            <tr key={company.id} className="hover:bg-blue-50/30 transition-colors group">
                <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                            <Building2 size={16} className="text-white" />
                        </div>
                        <div>
                            <div className="font-semibold text-[11px] text-gray-900">{company.name}</div>
                            <div className="flex flex-col gap-0.5 mt-0.5">
                                <div className="text-[10px] text-gray-500">ID: {String(company.id || '').substring(0, 8)}...</div>
                                {company.subdomain && (
                                    <div className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 py-0.5 rounded w-fit">
                                        {company.subdomain}.yaseero.ae
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-3 py-2">
                    <div className="font-mono text-[10px] text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {company.supplierId || 'N/A'}
                    </div>
                </td>
                <td className="px-3 py-2">
                    <div className="text-[11px] text-gray-900">{company.email}</div>
                </td>
                <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold ${plan?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                        plan?.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                            plan?.color === 'green' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                        }`}>
                        <CreditCard size={10} className="mr-1" />
                        {plan?.name || company.subscriptionPlan}
                    </span>
                </td>
                <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold ${getCompanyStatusColor(company.status)}`}>
                        {getCompanyStatusLabel(company.status)}
                    </span>
                </td>
                <td className="px-3 py-2">
                    <div className="space-y-1">
                        <div className="flex items-center gap-1 text-[10px] text-gray-600">
                            <Users size={10} className="text-blue-500" />
                            <span className="font-medium">{company.maxUsers || 0}</span>
                            <span className="text-gray-400">users</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-gray-600">
                            <span className="text-green-500">📦</span>
                            <span className="font-medium">{company.maxProducts || 0}</span>
                            <span className="text-gray-400">products</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-gray-600">
                            <span className="text-purple-500">📋</span>
                            <span className="font-medium">{company.maxOrders || 0}</span>
                            <span className="text-gray-400">orders</span>
                        </div>
                    </div>
                </td>
                <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                        <button
                            onClick={() => handleEditCompany(company)}
                            className="p-1.5 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-all group/edit relative"
                            title="Edit Company"
                        >
                            <Edit size={14} />
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/edit:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-medium">
                                Edit Company
                            </span>
                        </button>
                        <button
                            onClick={() => handleViewCompany(company)}
                            className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-all group/view relative"
                            title="View Company"
                        >
                            <Eye size={14} />
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/view:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-medium">
                                View Details
                            </span>
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    const renderCard = (company) => {
        const plan = getPlanById(company.subscriptionPlan);
        
        return (
            <div key={company.id} className="bg-white rounded-xl border border-gray-150 p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col gap-3 text-left">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shrink-0">
                            <Building2 size={20} className="text-white" />
                        </div>
                        <div>
                            <h4 className="font-bold text-[13px] text-gray-900 leading-tight">{company.name}</h4>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <div className="text-[10px] text-gray-500">ID: {String(company.id || '').substring(0, 8)}...</div>
                                {company.subdomain && (
                                    <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 py-0.5 rounded">
                                        {company.subdomain}.yaseero.ae
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold ${getCompanyStatusColor(company.status)}`}>
                        {getCompanyStatusLabel(company.status)}
                    </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[11px] bg-gray-50/70 p-2 rounded-lg border border-gray-100">
                    <div>
                        <span className="text-gray-400 block text-[9px] uppercase font-bold tracking-wider">Supplier ID</span>
                        <span className="font-mono text-gray-700 bg-gray-100/80 px-1.5 py-0.5 rounded text-[10px] inline-block mt-0.5">
                            {company.supplierId || 'N/A'}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-400 block text-[9px] uppercase font-bold tracking-wider">Plan</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5 ${
                            plan?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                            plan?.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                            plan?.color === 'green' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                        }`}>
                            <CreditCard size={10} className="mr-1" />
                            {plan?.name || company.subscriptionPlan}
                        </span>
                    </div>
                </div>

                <div className="text-[11px] text-gray-600 flex items-center gap-1">
                    <span className="font-semibold text-gray-500">Email:</span>
                    <span className="truncate">{company.email}</span>
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 pt-2.5 mt-1">
                    <div className="flex gap-3 text-[10px]">
                        <div className="flex items-center gap-1 text-gray-600" title="Max Users">
                            <Users size={12} className="text-blue-500" />
                            <span className="font-bold text-gray-800">{company.maxUsers || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600" title="Max Products">
                            <span>📦</span>
                            <span className="font-bold text-gray-800">{company.maxProducts || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600" title="Max Orders">
                            <span>📋</span>
                            <span className="font-bold text-gray-800">{company.maxOrders || 0}</span>
                        </div>
                    </div>
                    
                    <div className="flex gap-1.5">
                        <button
                            onClick={() => handleEditCompany(company)}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-all"
                        >
                            <Edit size={12} />
                            Edit
                        </button>
                        <button
                            onClick={() => handleViewCompany(company)}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-all"
                        >
                            <Eye size={12} />
                            View Details
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Empty state
    const emptyState = (
        <div className="text-center py-12">
            <Building2 size={48} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-1">No companies found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filter</p>
        </div>
    );

    return (
        <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-2 overflow-hidden overflow-x-hidden">
            {/* Header */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Companies
                        </h1>
                        <p className="text-gray-500 text-[10px]">Manage all registered companies and subscriptions</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={handleAddCompany}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all font-semibold"
                        >
                            <Plus size={12} />
                            Register Company
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                        <div className="relative">
                            <button
                                onClick={() => setShowPlanDropdown(!showPlanDropdown)}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-medium text-gray-700"
                            >
                                <CreditCard size={12} />
                                Plan: {selectedPlan === 'All' ? 'All' : getPlanById(selectedPlan)?.name || selectedPlan}
                                <ChevronDown size={12} className={`transition-transform ${showPlanDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showPlanDropdown && (
                                <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                                    {plans.map(plan => (
                                        <button
                                            key={plan}
                                            onClick={() => {
                                                setSelectedPlan(plan);
                                                setShowPlanDropdown(false);
                                                setCurrentPage(1);
                                            }}
                                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedPlan === plan
                                                ? 'bg-blue-50 text-blue-600 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {plan === 'All' ? 'All Plans' : getPlanById(plan)?.name || plan}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-medium text-gray-700"
                            >
                                <Filter size={12} />
                                Status: {selectedStatus === 'All' ? 'All' : getCompanyStatusLabel(selectedStatus)}
                                <ChevronDown size={12} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showStatusDropdown && (
                                <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                                    {statuses.map(status => (
                                        <button
                                            key={status}
                                            onClick={() => {
                                                setSelectedStatus(status);
                                                setShowStatusDropdown(false);
                                                setCurrentPage(1);
                                            }}
                                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedStatus === status
                                                ? 'bg-blue-50 text-blue-600 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {status === 'All' ? 'All Statuses' : getCompanyStatusLabel(status)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {(searchTerm || selectedPlan !== 'All' || selectedStatus !== 'All') && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedPlan('All');
                                setSelectedStatus('All');
                                setCurrentPage(1);
                            }}
                            className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700 font-bold uppercase tracking-wider"
                        >
                            <RefreshCw size={12} />
                            Reset Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Data Table */}
            <div className="flex-1 mb-1">
                <DataTable
                    data={filteredCompanies}
                    columns={columns}
                    renderRow={renderRow}
                    renderCard={renderCard}
                    searchPlaceholder="Search companies by name, email, supplier ID..."
                    searchFilter={searchFilter}
                    emptyState={emptyState}
                    defaultItemsPerPage={pageSize}
                    serverSide={true}
                    totalElements={totalElements}
                    searchTerm={searchTerm}
                    currentPage={currentPage}
                    loading={loading}
                    error={error}
                    onSearch={(val) => {
                        setSearchTerm(val);
                        setCurrentPage(1);
                    }}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            </div>

            {/* Modals */}
            {editCompany && (
                <CompanyEditModal
                    company={editCompany}
                    onClose={() => setEditCompany(null)}
                    onSave={handleSaveCompany}
                />
            )}

            {successCompany && (
                <RegistrationSuccessModal
                    company={successCompany}
                    onClose={() => setSuccessCompany(null)}
                />
            )}

            <DeleteConfirmModal
                isOpen={!!deleteCompany}
                onClose={() => setDeleteCompany(null)}
                onConfirm={confirmDelete}
                entityName="Company"
                itemName={deleteCompany?.name}
            />
        </div>
    );
};

export default CompaniesView;
